import React from 'react';

const DEFAULT_SKELETON_ROW_COUNT = 10;
const CARD_CLASS =
  'h-[35rem] overflow-hidden rounded-2xl border border-neutral-b4 bg-white p-6 flex flex-col gap-2';
const TABLE_HEADER_CELL_CLASS = 'p-2';

type AdminSkeletonProps = {
  rowCount?: number;
  variant?: 'card' | 'kpiTiles' | 'rows';
};

const renderSkeletonRows = (rowCount: number) =>
  Array.from({ length: rowCount }, (_, index) => (
    <tr
      key={index}
      aria-hidden
      data-testid="organizer-loading-row"
      className="border-b border-neutral-b4 last:border-b-0"
    >
      <td className="p-2 align-middle">
        <div className="h-6 w-6 animate-pulse rounded-full bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-10 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="h-4 w-10 animate-pulse rounded bg-gray-200" />
      </td>
      <td className="p-2 align-middle">
        <div className="flex gap-1">
          <div className="h-5 w-16 animate-pulse rounded bg-gray-200" />
          <div className="h-5 w-14 animate-pulse rounded bg-gray-200" />
        </div>
      </td>
    </tr>
  ));

export const AdminSkeleton = ({
  rowCount = DEFAULT_SKELETON_ROW_COUNT,
  variant = 'card',
}: AdminSkeletonProps) => {
  if (variant === 'rows') {
    return <>{renderSkeletonRows(rowCount)}</>;
  }

  if (variant === 'kpiTiles') {
    return (
      <div className="h-36 flex-1 animate-pulse rounded-2xl bg-gray-200" />
    );
  }

  return (
    <div
      className={CARD_CLASS}
      aria-busy={true}
      data-testid="events-by-organizer-card-skeleton"
    >
      <div className="shrink-0 flex items-center justify-between">
        <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
        <div className="h-6 w-6 animate-pulse rounded-full bg-gray-200" />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-9" />
            <col className="w-36" />
            <col className="w-52" />
            <col className="w-24" />
            <col className="w-44" />
            <col className="w-48" />
            <col className="w-32" />
            <col className="w-32" />
            <col />
          </colgroup>
          <thead>
            <tr className="border-b border-neutral-b4">
              <th className={TABLE_HEADER_CELL_CLASS} />
              {Array.from({ length: 8 }, (_, index) => (
                <th key={index} className={TABLE_HEADER_CELL_CLASS}>
                  <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderSkeletonRows(rowCount)}</tbody>
        </table>
      </div>
    </div>
  );
};
